/* eslint-disable no-unused-vars, default-case, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, Component } from 'react';
import logoImage from '../assets/25YearsLogo.png';
import './VideoConsultation.css';
import ConsultationHeader from './ConsultationHeader';
import ConsultationFooter from './ConsultationFooter';
import AppointmentService from '../utils/appointmentService';
import { theme } from '../theme/colors';
import ZegoUIKitPrebuilt from '@zegocloud/zego-uikit-prebuilt';

// Fetch Zego kit token from server only; send consultation token when present (VAPT: server validates OTP).
const fetchZegoKitToken = async (roomID, userID, userName) => {
  const base = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
  const url = base ? `${base}/api/zego-token` : '/api/zego-token';
  const consultationToken = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('consultationAccessToken') : null;
  const consultationLinkHash = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('consultationLinkHash') : null;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(consultationToken ? { 'X-Consultation-Token': consultationToken } : {}),
      ...(consultationLinkHash ? { 'X-Consultation-Link': consultationLinkHash } : {})
    },
    body: JSON.stringify({ roomID, userID, userName: userName || 'Patient' })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success || !data.token || data.appId == null) {
    throw new Error(data.error || 'Failed to get video session token.');
  }
  return { token: data.token, appId: data.appId };
};
// Error Boundary to catch DOM conflicts
class VideoErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    
    // If it's any DOM-related error, prevent it from crashing the app
    if (error.message && (
      error.message.includes('removeChild') ||
      error.message.includes('appendChild') ||
      error.message.includes('insertBefore') ||
      error.message.includes('replaceChild') ||
      error.message.includes('Node') ||
      error.message.includes('DOM') ||
      error.message.includes('NotFoundError')
    )) {
      
      // Try to recover by forcing a re-render
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 500);
      
      return;
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '20px',
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: '8px',
          margin: '20px'
        }}>
          <h3>Video Interface Error</h3>
          <p>There was an issue with the video interface. Please refresh the page.</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Isolated Zego Component to prevent DOM conflicts
const ZegoVideoInterface = ({ containerRef, isInitialized, initializationError, appointmentData, onRetry, showLeaveRoomPopup, onConfirmLeaveRoom, onCancelLeaveRoom }) => {
  // Use React.useMemo to prevent unnecessary re-renders that might cause DOM conflicts
        const containerStyle = React.useMemo(() => ({
        width: '100vw',
        height: 'calc(100vh - 70px - 40px)',
        background: 'white',
        borderRadius: 0,
        overflow: 'hidden',
        marginTop: '70px',
        marginLeft: 0,
        marginRight: 0,
        position: 'relative',
        minHeight: 'calc(100vh - 110px)',
        maxHeight: 'calc(100vh - 110px)',
        isolation: 'isolate',
        display: 'block',
        boxShadow: 'none',
        border: 'none',
        padding: 0
      }), []);

  try {
    return (
    <div 
      ref={containerRef}
      className="zego-video-container"
      style={containerStyle}
    >
      {/* Debug info */}
      {/* <div style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        background: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '8px',
        fontSize: '12px',
        zIndex: 1000,
        fontFamily: 'monospace'
      }}> */}
        {/* Zego: {isInitialized ? '✅' : '⏳'} | 
        Error: {initializationError ? '❌' : '✅'} | 
        Container: {containerRef.current ? '✅' : '❌'} */}
      {/* </div> */}



      {/* Leave Room Popup - Inside video container */}
      {showLeaveRoomPopup && (
        <div className="video-leave-popup" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999999,
          padding: '20px'
        }}>
          <div className="video-leave-popup-content" style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            border: '2px solid #EE2D67'
          }}>
            <h2 style={{
              color: '#962067',
              fontSize: '24px',
              fontWeight: 700,
              margin: '0 0 16px 0',
              fontFamily: "'Poppins', sans-serif"
            }}>
              Leave the room
            </h2>
            
            <p style={{
              color: '#58595B',
              fontSize: '16px',
              lineHeight: 1.6,
              margin: '0 0 24px 0'
            }}>
              Are you sure to leave the room?
            </p>
            
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              <button 
                onClick={onCancelLeaveRoom}
                style={{
                  background: '#eeeeee',
                  color: '#58595B',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '100px'
                }}
              >
                Cancel
              </button>
              
              <button 
                onClick={() => {
                  alert('Confirm button clicked! Health packages page should appear now.');
                  onConfirmLeaveRoom();
                }}
                style={{
                  background: '#962067',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  minWidth: '100px',
                  boxShadow: '0 4px 12px rgba(150, 32, 103, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = '#7a1a52';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#962067';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fallback content while Zego loads or error state */}
      {!isInitialized && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#A23293',
          textAlign: 'center',
          zIndex: 1,
          maxWidth: '400px',
          padding: '20px'
        }}>
          <div style={{
            width: '96px',
            height: '48px',
            background: 'transparent',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            overflow: 'hidden'
          }}>
            <img 
              src={logoImage} 
              alt="Kauvery Hospital Logo" 
              style={{
                width: '100%',
                height: '100%',
                // objectFit: 'contain'
              }}
            />
          </div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>Kauvery Hospital</h3>
          {initializationError ? (
            <div>
              <p style={{ margin: '0 0 12px 0', opacity: 0.9, color: '#ffcdd2' }}>
                Connection Error
              </p>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', opacity: 0.8 }}>
                {initializationError}
              </p>
              <button 
                onClick={onRetry}
                style={{
                  background: 'linear-gradient(135deg, #FDB913, #EE2D67)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                🔄 Retry Connection
              </button>
            </div>
          ) : (
            <p style={{ margin: 0, opacity: 0.8 }}>Loading Video Consultation...</p>
          )}
        </div>
      )}
    </div>
    );
  } catch (error) {
    // Return a fallback UI if there's an error
    return (
      <div style={{
        width: '100%',
        height: 'calc(100vh - 80px - 32px)',
        background: 'white',
        borderRadius: 0,
        marginTop: '80px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#A23293',
        textAlign: 'center',
        boxShadow: 'none',
        border: 'none'
      }}>
        <div>
          <h3>Video Interface Error</h3>
          <p>There was an issue loading the video interface.</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              background: '#007bff',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }
};

// Define parsedParams globally at the top of the component
let globalParsedParams = {};

const VideoConsultation = () => {
  const [isInConsultation, setIsInConsultation] = useState(true);
  const [isPrejoinVisible, setIsPrejoinVisible] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState({
    microphone: 'checking',
    camera: 'checking'
  });
  const [joinButtonLoading, setJoinButtonLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'info' });
  const [appointmentData, setAppointmentData] = useState({}); // Change from [] to {}
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedReason, setAccessDeniedReason] = useState('');
  const [zegoInitialized, setZegoInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState(null);
  const [callEnded, setCallEnded] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  const [decodingError, setDecodingError] = useState(null);
  const [showLeaveRoomPopup, setShowLeaveRoomPopup] = useState(false);
  const [showHealthPackages, setShowHealthPackages] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [reconnectCount, setReconnectCount] = useState(0);
  const [participantCount, setParticipantCount] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [connectionStartTime, setConnectionStartTime] = useState(null);
  const [appointmentStored, setAppointmentStored] = useState(false);
  const [connectEventSent, setConnectEventSent] = useState(false);

  // Monitor health packages state changes
  React.useEffect(() => {
    if (showHealthPackages) {

    }
  }, [showHealthPackages]);
  useEffect(() => {
    updateStatusDisplay();
  }, [participantCount]);
  // Enhanced Zego override with confirm button focus
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Force confirm button color in our popup */
      button[onclick*="onConfirmLeaveRoom"], 
      button[onclick*="handleConfirmLeaveRoom"],
      button:contains("Confirm") {
        background: #962067 !important;
        background-color: #962067 !important;
        color: #ffffff !important;
        border: none !important;
        outline: none !important;
        text-decoration: none !important;
        box-shadow: 0 4px 12px rgba(150, 32, 103, 0.3) !important;
      }
      
      /* Hide Zego's end call popup */
      [class*="popup"], [class*="modal"], [class*="dialog"], [class*="overlay"],
      [class*="Popup"], [class*="Modal"], [class*="Dialog"], [class*="Overlay"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
      
      /* Override any blue confirm buttons */
      button[style*="blue"], button[style*="Blue"] {
        background: #962067 !important;
        background-color: #962067 !important;
        color: #ffffff !important;
      }
    `;
    document.head.appendChild(style);

    // Function to intercept Zego end call button and show our popup
    const interceptZegoEndCall = () => {
      // Find any end call buttons
      const endCallButtons = document.querySelectorAll('button, [class*="button"], [class*="btn"]');
      endCallButtons.forEach(button => {
        if (button.textContent && (
          button.textContent.toLowerCase().includes('end call') ||
          button.textContent.toLowerCase().includes('leave') ||
          button.textContent.toLowerCase().includes('quit') ||
          button.textContent.toLowerCase().includes('hang up')
        )) {
          
          // Remove existing click handlers
          button.replaceWith(button.cloneNode(true));
          const newButton = document.querySelector(`[class*="${button.className}"]`);
          
          if (newButton) {
            newButton.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              
              handleEndCall();
              return false;
            }, true);
          }
        }
      });
    };

    // Function to force confirm button color
    const forceConfirmButtonColor = () => {
      const confirmButtons = document.querySelectorAll('button');
      confirmButtons.forEach(button => {
        if (button.textContent && button.textContent.toLowerCase().includes('confirm')) {
          button.style.background = '#962067';
          button.style.backgroundColor = '#962067';
          button.style.color = '#ffffff';
          button.style.border = 'none';
          button.style.outline = 'none';
        }
      });
    };

    // Run interceptors
    interceptZegoEndCall();
    forceConfirmButtonColor();
    
    // Set up intervals
    const endCallInterval = setInterval(interceptZegoEndCall, 1000);
    const colorInterval = setInterval(forceConfirmButtonColor, 500);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      clearInterval(endCallInterval);
      clearInterval(colorInterval);
    };
  }, []);

  // Force button color when popup shows
  React.useEffect(() => {
    if (showLeaveRoomPopup) {
      const timer = setTimeout(() => {
        if (confirmButtonRef.current) {
          confirmButtonRef.current.style.background = '#962067';
          confirmButtonRef.current.style.backgroundColor = '#962067';
          confirmButtonRef.current.style.color = '#ffffff';
          confirmButtonRef.current.style.border = 'none';
          confirmButtonRef.current.style.outline = 'none';
        }
        
        // Also try via querySelector as backup
        const confirmButton = document.querySelector('.kauvery-confirm-button');
        if (confirmButton) {
          confirmButton.style.background = '#962067';
          confirmButton.style.backgroundColor = '#962067';
          confirmButton.style.color = '#ffffff';
          confirmButton.style.border = 'none';
          confirmButton.style.outline = 'none';
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [showLeaveRoomPopup]);

  const zegoContainerRef = useRef(null);
  const zegoInstanceRef = useRef(null);
  const confirmButtonRef = useRef(null);

  // Color scheme
  const colors = {
    kauveryPurple: '#962067',
    kauveryViolet: '#A23293',
    kauveryPink: '#EE2D67',
    kauveryYellow: '#FFE73F',
    kauveryDarkGrey: '#58595B',
    kauveryRed: '#EC1D25',
    kauveryOrange: '#F26522',
    kauveryYellowOrange: '#FDB913',
    kauveryPeach: '#FAA85F',
    kauveryLightGrey: '#939598',
    white: '#ffffff',
    black: '#000000',
    grey50: '#fafafa',
    grey100: '#f5f5f5',
    grey200: '#eeeeee',
    grey300: '#e0e0e0'
  };

  const spacing = {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px'
  };

  const radius = {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '24px',
    full: '50%'
  };

  const shadows = {
    sm: '0 1px 2px 0 rgba(88, 89, 91, 0.3), 0 1px 3px 1px rgba(88, 89, 91, 0.15)',
    md: '0 1px 2px 0 rgba(88, 89, 91, 0.3), 0 2px 6px 2px rgba(88, 89, 91, 0.15)',
    lg: '0 2px 4px 0 rgba(88, 89, 91, 0.3), 0 8px 12px 6px rgba(88, 89, 91, 0.15)'
  };

  // Health Package Data
  const healthPackages = [
    {
      id: 1,
      title: "Home Health Check Package Essential",
      icon: "👤",
      expanded: false,
      services: [
        "CBC (Complete Blood Count)",
        "Blood Glucose Random (RBS)",
        "HBA1C",
        "Renal Function Test",
        "Teleconsult with Physician"
      ]
    },
    {
      id: 2,
      title: "Home Health Check Package Standard",
      icon: "💻",
      expanded: false,
      services: [
        "Complete Blood Count",
        "Blood Glucose Fasting & PP",
        "Lipid Profile",
        "Liver Function Test",
        "Kidney Function Test",
        "ECG",
        "Teleconsult with Physician"
      ]
    },
    {
      id: 3,
      title: "Home Health Check Package Comprehensive",
      icon: "💻",
      expanded: false,
      services: [
        "Complete Blood Count",
        "Blood Glucose Fasting & PP",
        "Lipid Profile",
        "Liver Function Test",
        "Kidney Function Test",
        "Thyroid Function Test",
        "ECG",
        "Chest X-Ray",
        "Ultrasound Abdomen",
        "Teleconsult with Physician"
      ]
    },
    {
      id: 4,
      title: "Life style health Check Essential",
      icon: "👤",
      expanded: false,
      services: [
        "BMI Calculation",
        "Blood Pressure Monitoring",
        "Blood Glucose Test",
        "Cholesterol Screening",
        "Lifestyle Counseling"
      ]
    },
    {
      id: 5,
      title: "Life style health Check Standard",
      icon: "👤",
      expanded: false,
      services: [
        "BMI Calculation",
        "Blood Pressure Monitoring",
        "Blood Glucose Test",
        "Cholesterol Screening",
        "ECG",
        "Lifestyle Counseling",
        "Nutrition Consultation"
      ]
    },
    {
      id: 6,
      title: "Life style health Check Comprehensive",
      icon: "👤",
      expanded: false,
      services: [
        "BMI Calculation",
        "Blood Pressure Monitoring",
        "Blood Glucose Test",
        "Cholesterol Screening",
        "ECG",
        "Chest X-Ray",
        "Lifestyle Counseling",
        "Nutrition Consultation",
        "Fitness Assessment"
      ]
    },
    {
      id: 7,
      title: "Executive Health Check - Male",
      icon: "👨‍⚕️",
      expanded: false,
      services: [
        "Complete Blood Count",
        "Blood Glucose Fasting & PP",
        "Lipid Profile",
        "Liver Function Test",
        "Kidney Function Test",
        "Thyroid Function Test",
        "PSA Test",
        "ECG",
        "Chest X-Ray",
        "Ultrasound Abdomen",
        "Stress Test",
        "Consultation with Specialist"
      ]
    },
    {
      id: 8,
      title: "Executive Health Check - Female",
      icon: "👩‍⚕️",
      expanded: false,
      services: [
        "Complete Blood Count",
        "Blood Glucose Fasting & PP",
        "Lipid Profile",
        "Liver Function Test",
        "Kidney Function Test",
        "Thyroid Function Test",
        "Pap Smear",
        "Mammography",
        "ECG",
        "Chest X-Ray",
        "Ultrasound Abdomen",
        "Stress Test",
        "Consultation with Specialist"
      ]
    }
  ];

  // Pure React approach - no DOM manipulation
  useEffect(() => {
    applyKauveryStyles();
    
      // Clean up only floating info (in-room panel); keep pre-join participant info (Doctor/Patient card)
  const existingFloatingOnly = document.querySelectorAll('.kauvery-floating-info');
  existingFloatingOnly.forEach(info => {
    if (info && info.parentNode) {
      try {
        info.parentNode.removeChild(info);
      } catch (error) {
        info.style.display = 'none';
        info.style.visibility = 'hidden';
        info.style.opacity = '0';
      }
    }
  });
    
    // Always apply customization on mount so updated design reflects reliably
    
    // Override React's DOM manipulation to prevent conflicts
    const originalRemoveChild = Node.prototype.removeChild;
    const originalAppendChild = Node.prototype.appendChild;
    const originalInsertBefore = Node.prototype.insertBefore;
    
    // Safe removeChild that doesn't throw errors
    Node.prototype.removeChild = function(child) {
      try {
        // Always check if child exists and has a parent
        if (!child) {
          return null;
        }
        
        // Check if child is actually a child of this node
        if (child.parentNode === this) {
          return originalRemoveChild.call(this, child);
        }
        
        // If child is not a direct child, try to remove it from its actual parent
        if (child.parentNode) {
          return child.parentNode.removeChild(child);
        }
        
        // If child has no parent, it's already removed
        return child;
      } catch (error) {
        // Return null to prevent further errors
        return null;
      }
    };
    
    // Safe appendChild that doesn't throw errors
    Node.prototype.appendChild = function(child) {
      try {
        if (!child) {
          return null;
        }
        
        // Check if child is already a child of this node
        if (child.parentNode === this) {
          return child;
        }
        
        return originalAppendChild.call(this, child);
      } catch (error) {
        return null;
      }
    };
    
    // Safe insertBefore that doesn't throw errors
    Node.prototype.insertBefore = function(newNode, referenceNode) {
      try {
        if (!newNode) {
          return null;
        }
        
        // Check if newNode is already a child of this node
        if (newNode.parentNode === this) {
          return newNode;
        }
        
        return originalInsertBefore.call(this, newNode, referenceNode);
      } catch (error) {
        return null;
      }
    };
    
    // Enhanced global error handler to catch removeChild errors
    const handleGlobalError = (event) => {
      // Catch ALL DOM manipulation errors aggressively
      if (event.error && event.error.message && (
        event.error.message.includes('removeChild') ||
        event.error.message.includes('appendChild') ||
        event.error.message.includes('insertBefore') ||
        event.error.message.includes('replaceChild') ||
        event.error.message.includes('Node') ||
        event.error.message.includes('DOM') ||
        event.error.message.includes('NotFoundError')
      )) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        return false;
      }
    };
    
    // Enhanced unhandled promise rejections handler
    const handleUnhandledRejection = (event) => {
      if (event.reason && event.reason.message && (
        event.reason.message.includes('removeChild') ||
        event.reason.message.includes('NotFoundError')
      )) {
        event.preventDefault();
        return false;
      }
      
      // Also catch DOM manipulation promise rejections
      if (event.reason && event.reason.message && (
        event.reason.message.includes('removeChild') ||
        event.reason.message.includes('appendChild') ||
        event.reason.message.includes('insertBefore') ||
        event.reason.message.includes('replaceChild') ||
        event.reason.message.includes('NotFoundError')
      )) {
        event.preventDefault();
        return false;
      }
    };
    
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      
      // Disconnect observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      
      // Clean up any remaining consultation details
      const remainingInfo = document.querySelectorAll('.kauvery-participant-info, .kauvery-floating-info');
      remainingInfo.forEach(info => {
        if (info && info.parentNode) {
          try {
            info.parentNode.removeChild(info);
          } catch (error) {
            info.style.display = 'none';
          }
        }
      });
      
      // Restore original DOM methods
      if (originalRemoveChild) {
        Node.prototype.removeChild = originalRemoveChild;
      }
      if (originalAppendChild) {
        Node.prototype.appendChild = originalAppendChild;
      }
      if (originalInsertBefore) {
        Node.prototype.insertBefore = originalInsertBefore;
      }
      
      // Cleanup Zego instance on unmount
      if (zegoInstanceRef.current) {
        try {
          zegoInstanceRef.current.leaveRoom();
          zegoInstanceRef.current = null;
        } catch (error) {
        }
      }
      
      // Reset global flag
      window.kauveryCustomizationDone = false;
    };
  }, []);

  useEffect(() => {
    try {
      // Get decrypted params from App.js storage
      const decryptedParams = sessionStorage.getItem("decryptedParams");
      
      if (decryptedParams) {
        const parsedParams = JSON.parse(decryptedParams);
        // Store globally so it's accessible everywhere
        globalParsedParams = parsedParams;
        
        // Generate room ID based on appointment ID (just the ID number)
        const appointmentId = AppointmentService.getAppointmentId();
        const roomId = parsedParams.app_no;

        // Set appointment data with decrypted values from App.js
        const appointmentDataToSet = {
          id: parsedParams.app_no,
          department: parsedParams.speciality,
          username: parsedParams.username,
          userid: parsedParams.userid,
          roomID: roomId, // Use just appointment ID as room ID
          doctorName: parsedParams.doctorname,
          status: 'Scheduled'
        };
        
        setAppointmentData(appointmentDataToSet);
      }
      } catch (error) {
      setAccessDenied(true);
      setAccessDeniedReason('Failed to load appointment data');
    }
  }, []);

  // Now you can use globalParsedParams anywhere in the component
  const getDoctorName = () => {
    return globalParsedParams.doctorname || 'Dr. Unknown';
  };

  const getPatientName = () => {
    return globalParsedParams.username || 'Patient';
  };

  // Use these functions in your display code
  const doctorName = getDoctorName();
  const patientName = getPatientName();

  // Separate useEffect to initialize Zego when appointment data is available
  useEffect(() => {
    if (appointmentData && appointmentData.roomID && !zegoInitialized && !zegoInstanceRef.current) {
      
      // Temporarily delay Zego initialization to ensure DOM is stable
      setTimeout(() => {
        initializeZego().catch(() => {});
      }, 1000);
    }
  }, [appointmentData]); // Run when appointment data changes

  // Debug useEffect to track zegoInitialized state changes
  useEffect(() => {

  }, [zegoInitialized, appointmentData]);

  // Styling marker: in-call mode (used by mobile CSS to keep controls/chat visible)
  useEffect(() => {
    document.body.classList.toggle('kauvery-in-call', Boolean(zegoInitialized));
    return () => document.body.classList.remove('kauvery-in-call');
  }, [zegoInitialized]);

  // Add this useEffect to log when appointmentData changes
  useEffect(() => {
    if (appointmentData && Object.keys(appointmentData).length > 0) {
    }
  }, [appointmentData]);

  const generateAppointmentId = () => {
    const prefix = 'KH';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substr(2, 3).toUpperCase();
    return `${prefix}${timestamp}${random}`;
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'info' });
    }, 4000);
  };

  const handleEndCall = () => {

    // Track end call event
    const appointmentId = AppointmentService.getAppointmentId();
    if (appointmentId && appointmentData) {
      // Store video call event
      AppointmentService.storeVideoCallEvent({
        appointment_id: parseInt(appointmentId),
        event_type: 'end_call_requested',
        roomID: appointmentData.roomID,
        user_id: appointmentData.userid,
        username: appointmentData.username,
        event_data: {
          timestamp: new Date().toISOString(),
          roomID: appointmentData.roomID,
          user_id: appointmentData.userid,
          username: appointmentData.username,
          action: 'user_requested_end_call'
        }
      }).catch(() => {});
    }
    
    setShowLeaveRoomPopup(true);
  };

  const handleConfirmLeaveRoom = () => {
    setShowLeaveRoomPopup(false);
    
    // Track manual leave room event
    const appointmentId = AppointmentService.getAppointmentId();
    if (appointmentId && appointmentData) {
      // End call session
      AppointmentService.endCallSession({
        appointment_id: parseInt(appointmentId)
      }).catch(() => {});
      
      // Store video call event
      AppointmentService.storeVideoCallEvent({
        appointment_id: parseInt(appointmentId),
        event_type: 'manual_leave_room',
        roomID: appointmentData.roomID,
        user_id: appointmentData.userid,
        username: appointmentData.username,
        event_data: {
          timestamp: new Date().toISOString(),
          roomID: appointmentData.roomID,
          user_id: appointmentData.userid,
          username: appointmentData.username,
          action: 'user_confirmed_leave'
        }
      }).catch(() => {});
    }
    
    if (zegoInstanceRef.current) {
      try {
        zegoInstanceRef.current.leaveRoom();
      } catch (error) {
      }
    }
    setCallEnded(true);
    setZegoInitialized(false);
    // Redirect to Kauvery Hospital and clear session
    setTimeout(completeSessionAndRedirect, 300);
  };

  const handleCancelLeaveRoom = () => {
    setShowLeaveRoomPopup(false);
  };

  const checkDevices = async () => {
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      setDeviceStatus({ microphone: 'ready', camera: 'ready' });
      showNotification('All devices are ready for your consultation', 'success');
    } catch (error) {
      showNotification('Please check your camera and microphone permissions', 'error');
    }
  };

  // Store observer reference for cleanup
  const observerRef = React.useRef(null);
  
  // Customize pre-join view with participant info and button text
  const customizePreJoinView = () => {
    try {
      
      // Find and update join button text with slight curve corners
      const buttons = document.querySelectorAll('button');
      buttons.forEach(button => {
        if (button.textContent && (
          button.textContent.toLowerCase().includes('join') ||
          button.textContent.toLowerCase().includes('start') ||
          button.textContent.toLowerCase().includes('enter')
        )) {
          button.textContent = 'Join Teleconsultation';
          button.style.background = 'linear-gradient(135deg, #A23293, #EE2D67)';
          button.style.color = 'white';
          button.style.fontFamily = "'Poppins', sans-serif";
          button.style.fontWeight = '600';
          button.style.borderRadius = '20px'; // More rounded corners
          button.style.padding = '12px 24px';
          button.style.border = 'none';
          button.style.cursor = 'pointer';
          button.style.transition = 'all 0.3s ease';
          button.style.boxShadow = '0 2px 8px rgba(162, 50, 147, 0.2)';
          button.style.marginLeft = '30px';
        }
      });
      
      // Try to find and modify existing Zego participant elements
      const modifyExistingElements = () => {
        // Look for existing participant or user elements
        const participantElements = document.querySelectorAll('[class*="participant"], [class*="user"], [class*="member"], [class*="attendee"], [class*="avatar"]');
        
        participantElements.forEach((element, index) => {
          // Apply rounded styling to existing elements
          element.style.borderRadius = '20px !important';
          element.style.background = 'linear-gradient(135deg, rgba(162, 50, 147, 0.08), rgba(238, 45, 103, 0.08)) !important';
          element.style.border = '1px solid rgba(162, 50, 147, 0.15) !important';
          element.style.padding = '16px !important';
          element.style.boxShadow = '0 3px 10px rgba(162, 50, 147, 0.08) !important';
          
          // Find and modify any circular elements (avatars/icons)
          const circularElements = element.querySelectorAll('[class*="circle"], [class*="avatar"], [class*="icon"]');
          circularElements.forEach(circle => {
            circle.style.borderRadius = '50% !important';
            circle.style.overflow = 'hidden !important';
            circle.style.width = '40px !important';
            circle.style.height = '40px !important';
          });
        });
        
        // Also look for any divs that might be participant containers
        const allDivs = document.querySelectorAll('div');
        allDivs.forEach(div => {
          const computedStyle = window.getComputedStyle(div);
          const hasGradient = computedStyle.background.includes('gradient') || 
                            computedStyle.background.includes('rgb') ||
                            div.style.background.includes('gradient');
          
          // If it looks like a participant container, apply our styling
          if (hasGradient && div.children.length > 0) {
            div.style.borderRadius = '20px !important';
            div.style.background = 'linear-gradient(135deg, rgba(162, 50, 147, 0.08), rgba(238, 45, 103, 0.08)) !important';
            div.style.border = '1px solid rgba(162, 50, 147, 0.15) !important';
            div.style.padding = '16px !important';
            div.style.boxShadow = '0 3px 10px rgba(162, 50, 147, 0.08) !important';
          }
        });
      };
      
      // Try to modify existing elements first
      modifyExistingElements();
      
      // Also try to add our custom section as a fallback
      let prejoinContainer = document.querySelector('[class*="prejoin"], [class*="Prejoin"], [class*="PreJoin"]');
      const joinButton = Array.from(document.querySelectorAll('button')).find(button => {
        const txt = (button.textContent || '').toLowerCase();
        return txt.includes('join') || txt.includes('start') || txt.includes('enter');
      });
      
      // If not found, try to find any container with buttons
      if (!prejoinContainer && joinButton) {
        prejoinContainer = joinButton.closest('[class*="container"], [class*="view"], [class*="panel"], [class*="content"]') || joinButton.parentElement;
      }
      
      // Fallback to body if still not found
      if (!prejoinContainer) {
        prejoinContainer = document.body;
      }
      
      if (prejoinContainer) {
        // Remove existing participant info globally to avoid duplicates
        document.querySelectorAll('.kauvery-participant-info').forEach(el => el.remove());
        
        // Also remove any floating participant info
        const existingFloatingInfo = document.querySelector('.kauvery-floating-info');
        if (existingFloatingInfo) {
          existingFloatingInfo.remove();
        }

        // Mobile-only: keep only Join button, do not render Doctor/Patient details card
        const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
        if (isMobileViewport) {
          if (joinButton) {
            joinButton.style.display = 'block';
            joinButton.style.visibility = 'visible';
            joinButton.style.opacity = '1';
            joinButton.style.pointerEvents = 'auto';
            joinButton.style.width = '100%';
            joinButton.style.maxWidth = '360px';
            joinButton.style.margin = '12px auto';
          }
          return;
        }
        
        // Create participant info section with higher z-index and more prominent styling
        const participantInfo = document.createElement('div');
        participantInfo.className = 'kauvery-participant-info';
        participantInfo.style.cssText = `
          background: rgba(255, 255, 255, 0.95) !important;
          border: 2px solid rgba(162, 50, 147, 0.2) !important;
          border-radius: 20px !important;
          padding: 20px !important;
          margin: 20px auto !important;
          text-align: center !important;
          font-family: 'Poppins', sans-serif !important;
          box-shadow: 0 8px 25px rgba(162, 50, 147, 0.15) !important;
          backdrop-filter: blur(10px) !important;
          position: relative !important;
          z-index: 9999 !important;
          max-width: 400px !important;
          width: 90% !important;
          height: fit-content !important;
          transform: scale(1) !important;
          transition: all 0.3s ease !important;
          align-self: center !important;
          justify-self: center !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        `;
        
        // Get appointment details for display
        const doctorName = globalParsedParams? globalParsedParams.doctorname : '';
        const department = globalParsedParams? globalParsedParams.speciality : '';
        const patientName = globalParsedParams? globalParsedParams.username : '';
        
                participantInfo.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            gap: 20px;
            text-align: center;
            width: 100%;
          ">
            <!-- Patient Details Section -->
            <div style="
              display: flex;
              flex-direction: row;
              gap: 20px;
              justify-content: center;
              align-items: center;
            ">
              <!-- Doctor Info -->
              <div style="
                background: linear-gradient(135deg, rgba(162, 50, 147, 0.1), rgba(238, 45, 103, 0.1)) !important;
                border: 2px solid rgba(162, 50, 147, 0.2) !important;
                border-radius: 25px !important;
                padding: 20px !important;
                transition: all 0.3s ease !important;
                box-shadow: 0 4px 15px rgba(162, 50, 147, 0.1) !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                gap: 10px !important;
                min-width: 130px !important;
                width: 130px !important;
                height: auto !important;
              ">
                <div style="text-align: center;">
                  <p style="
                    color: #A23293 !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    margin: 0 0 5px 0 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.5px !important;
                    font-family: 'Poppins', sans-serif !important;
                  ">Doctor</p>
                  <p style="
                    color: #333 !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    margin: 0 !important;
                    font-family: 'Poppins', sans-serif !important;
                  ">${doctorName}</p>
                </div>
              </div>
              
              <!-- Patient Info -->
              <div style="
                background: linear-gradient(135deg, rgba(162, 50, 147, 0.1), rgba(238, 45, 103, 0.1)) !important;
                border: 2px solid rgba(162, 50, 147, 0.2) !important;
                border-radius: 25px !important;
                padding: 20px !important;
                transition: all 0.3s ease !important;
                box-shadow: 0 4px 15px rgba(162, 50, 147, 0.1) !important;
                display: flex !important;
                flex-direction: column !important;
                align-items: center !important;
                gap: 10px !important;
                min-width: 130px !important;
                width: 130px !important;
                height: auto !important;
              ">
                <div style="text-align: center;">
                  <p style="
                    color: #A23293 !important;
                    font-size: 11px !important;
                    font-weight: 700 !important;
                    margin: 0 0 5px 0 !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.5px !important;
                    font-family: 'Poppins', sans-serif !important;
                  ">Patient</p>
                  <p style="
                    color: #333 !important;
                    font-size: 14px !important;
                    font-weight: 600 !important;
                    margin: 0 !important;
                    font-family: 'Poppins', sans-serif !important;
                  ">${patientName}</p>
                </div>
              </div>
            </div>
            
            <!-- Real-time Room Status -->
            <div id="room-status" style="
              background: linear-gradient(135deg, rgba(40, 167, 69, 0.15), rgba(40, 167, 69, 0.1)) !important;
              border: 2px solid rgba(40, 167, 69, 0.3) !important;
              border-radius: 20px !important;
              padding: 18px !important;
              position: relative !important;
              overflow: hidden !important;
              box-shadow: 0 4px 15px rgba(40, 167, 69, 0.15) !important;
            ">
              <div style="
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                height: 3px;
                background: linear-gradient(90deg, #28a745, #20c997);
              "></div>
              <div id="status-content" style="
                color: #155724 !important;
                font-size: 13px !important;
                margin: 0 !important;
                font-family: 'Poppins', sans-serif !important;
                font-weight: 600 !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                gap: 10px !important;
              ">
                <span style="
                  width: 10px !important;
                  height: 10px !important;
                  background: linear-gradient(135deg, #28a745, #20c997) !important;
                  border-radius: 50% !important;
                  display: inline-block !important;
                  animation: pulse 2s infinite !important;
                  box-shadow: 0 0 8px rgba(40, 167, 69, 0.4) !important;
                "></span>
                <span id="status-text">🟢 Checking room status...</span>
              </div>
              <div id="participants-list" style="
                margin-top: 15px;
                display: none;
                flex-direction: column;
                gap: 10px;
              ">
                <!-- Participants will be dynamically added here -->
              </div>
            </div>
          </div>
        `;
        
        // Insert the participant info under Join button area (same as previous design screenshot)
        const joinHost = joinButton
          ? (joinButton.closest('[class*="content"], [class*="panel"], [class*="container"], [class*="view"]') || joinButton.parentElement || prejoinContainer)
          : prejoinContainer;
        if (joinButton && joinButton.parentNode) {
          joinButton.insertAdjacentElement('afterend', participantInfo);
        } else {
          joinHost.appendChild(participantInfo);
        }
        
              // Start monitoring room status
      updateRoomStatus();
      
        // Set up a mutation observer to reapply styling when Zego updates the DOM
        const observer = new MutationObserver(() => {
          setTimeout(() => {
            modifyExistingElements();
          }, 100);
        });
        
        observer.observe(prejoinContainer, {
          childList: true,
          subtree: true,
          attributes: true
        });
        
        // Store observer reference for cleanup
        observerRef.current = observer;
      }
      
      // ... rest of the existing code ...
      } catch (error) {
      }
  };
  
  // Force remove all borders from pre-join view
  const forceRemoveBorders = () => {
    // Remove borders from all elements immediately
    const allElements = document.querySelectorAll('*');
    allElements.forEach(element => {
      element.style.border = 'none';
      element.style.borderWidth = '0';
      element.style.borderStyle = 'none';
      element.style.borderColor = 'transparent';
      element.style.borderRadius = '0';
      element.style.outline = 'none';
      element.style.outlineWidth = '0';
      element.style.boxShadow = 'none';
    });
    
    // Continue removing borders every 50ms
    setInterval(() => {
      const allElements = document.querySelectorAll('*');
      allElements.forEach(element => {
        element.style.border = 'none';
        element.style.borderWidth = '0';
        element.style.borderStyle = 'none';
        element.style.borderColor = 'transparent';
        element.style.borderRadius = '0';
        element.style.outline = 'none';
        element.style.outlineWidth = '0';
        element.style.boxShadow = 'none';
      });
    }, 50);
  };

  // Update room status with real-time participant information
  const updateRoomStatus = () => {
    try {
      const statusText = document.getElementById('status-text');
      const participantsList = document.getElementById('participants-list');
      
      if (!statusText || !participantsList) {
        setTimeout(updateRoomStatus, 1000);
        return;
      }
      
      // Check for Zego participants in the DOM
      const zegoParticipants = document.querySelectorAll('[class*="participant"], [class*="user"], [class*="member"], [class*="attendee"]');
      const zegoUserNames = document.querySelectorAll('[class*="username"], [class*="name"], [class*="display-name"]');
      
      // Also check for any video elements that might indicate participants
      const videoElements = document.querySelectorAll('video');
      const audioElements = document.querySelectorAll('audio');
      
      // Count active participants
      let currentParticipantCount = participantCount; // Use the state variable from onUserUpdate
      let participantNames = [];
      
      // Try to extract names from DOM for display purposes only
        zegoUserNames.forEach(nameElement => {
          const name = nameElement.textContent?.trim();
          if (name && name !== 'You' && name !== 'Me' && !participantNames.includes(name)) {
            participantNames.push(name);
          }
        });
      
      // Update status display
      if (currentParticipantCount === 0) {
        statusText.textContent = 'No one else in the room';
        participantsList.style.display = 'none';
      } else {
        // Show participant names in status
        if (participantNames.length > 0) {
          if (participantNames.length === 1) {
            statusText.textContent = `${participantNames[0]} is in the room`;
          } else {
            const lastParticipant = participantNames[participantNames.length - 1];
            const otherParticipants = participantNames.slice(0, -1);
            if (otherParticipants.length === 1) {
              statusText.textContent = `${otherParticipants[0]} and ${lastParticipant} are in the room`;
            } else {
              statusText.textContent = `${otherParticipants.join(', ')}, and ${lastParticipant} are in the room`;
            }
          }
        } else {
          statusText.textContent = `${currentParticipantCount} participant${currentParticipantCount > 1 ? 's' : ''} in room`;
        }
        participantsList.style.display = 'flex';
        
        // Clear existing list
        participantsList.innerHTML = '';
        
        // Add participant names
        participantNames.forEach(name => {
          const participantItem = document.createElement('div');
          participantItem.style.cssText = `
            background: rgba(40, 167, 69, 0.1);
            border: 1px solid rgba(40, 167, 69, 0.2);
            border-radius: 8px;
            padding: 8px 12px;
            font-size: 11px;
            color: #155724;
            font-family: 'Poppins', sans-serif;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 8px;
          `;
          participantItem.innerHTML = `
            <span style="
              width: 6px;
              height: 6px;
              background: #28a745;
              border-radius: 50%;
              display: inline-block;
            "></span>
            <span>${name}</span>
          `;
          participantsList.appendChild(participantItem);
        });
      }
      
      // Continue monitoring
      setTimeout(updateRoomStatus, 2000);
      
    } catch (error) {
      // Retry after error
      setTimeout(updateRoomStatus, 3000);
    }
  };

  // Create floating participant info panel
  const createFloatingParticipantInfo = () => {
    try {
      // Remove existing floating info
      const existingFloating = document.querySelector('.kauvery-floating-info');
      if (existingFloating) {
        existingFloating.remove();
      }
      
      // Create floating container
      const floatingInfo = document.createElement('div');
      floatingInfo.className = 'kauvery-floating-info';
      floatingInfo.style.cssText = `
        position: fixed;
        top: 100px;
        left: 50%;
        transform: translateX(-50%);
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 249, 250, 0.98));
        border: 1px solid #A23293;
        border-radius: 8px;
        padding: 15px;
        text-align: center;
        font-family: 'Poppins', sans-serif;
        
        backdrop-filter: blur(8px);
        z-index: 9999;
        max-width: 380px;
        width: 80%;
        transform: translateX(-50%) scale(1);
        transition: all 0.3s ease;
      `;
      
      // Get appointment details for display
      const doctorName = appointmentData?.doctorName || '';
      const department = appointmentData?.department || 'General Medicine';
      const patientName = appointmentData?.username || 'Patient';
      
      floatingInfo.innerHTML = `
        <div style="
          margin-bottom: 15px;
          text-align: center;
          position: relative;
        ">
          <div style="
            width: 32px;
            height: 32px;
            background: linear-gradient(135deg, #A23293, #EE2D67);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 8px;
            box-shadow: 0 1px 4px rgba(162, 50, 147, 0.15);
          ">
            <span style="font-size: 14px; color: white;">📋</span>
          </div>
          <h3 style="
            color: #A23293;
            font-size: 16px;
            font-weight: 700;
            margin: 0 0 4px 0;
            font-family: 'Poppins', sans-serif;
            text-shadow: 0 1px 2px rgba(162, 50, 147, 0.1);
          ">Consultation Details</h3>
          <p style="
            color: #666;
            font-size: 12px;
            margin: 0;
            font-family: 'Poppins', sans-serif;
            font-weight: 500;
                      ">Room: ${appointmentData?.roomID}</p>
        </div>
        
        <div style="
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 15px;
          text-align: left;
        ">
          <div style="
            background: linear-gradient(135deg, rgba(162, 50, 147, 0.05), rgba(238, 45, 103, 0.05));
            border: 1px solid rgba(162, 50, 147, 0.1);
            border-radius: 8px;
            padding: 12px;
            transition: all 0.3s ease;
          ">
            <p style="
              color: #A23293;
              font-size: 9px;
              font-weight: 700;
              margin: 0 0 3px 0;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              font-family: 'Poppins', sans-serif;
            ">Doctor</p>
            <p style="
              color: #333;
              font-size: 12px;
              font-weight: 600;
              margin: 0 0 1px 0;
              font-family: 'Poppins', sans-serif;
            ">${doctorName}</p>
            <p style="
              color: #666;
              font-size: 10px;
              margin: 0;
              font-family: 'Poppins', sans-serif;
              font-weight: 500;
            ">${department}</p>
          </div>
          
          <div style="
            background: linear-gradient(135deg, rgba(162, 50, 147, 0.05), rgba(238, 45, 103, 0.05));
            border: 1px solid rgba(162, 50, 147, 0.1);
            border-radius: 8px;
            padding: 12px;
            transition: all 0.3s ease;
          ">
            <p style="
              color: #A23293;
              font-size: 9px;
              font-weight: 700;
              margin: 0 0 3px 0;
              text-transform: uppercase;
              letter-spacing: 0.3px;
              font-family: 'Poppins', sans-serif;
            ">Patient</p>
            <p style="
              color: #333;
              font-size: 12px;
              font-weight: 600;
              margin: 0 0 1px 0;
              font-family: 'Poppins', sans-serif;
            ">${patientName}</p>
            <p style="
              color: #666;
              font-size: 10px;
              margin: 0;
              font-family: 'Poppins', sans-serif;
              font-weight: 500;
            ">Ready to join</p>
          </div>
        </div>
        
        <div style="
          background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(40, 167, 69, 0.05));
          border: 1px solid rgba(40, 167, 69, 0.2);
          border-radius: 6px;
          padding: 8px;
          margin-top: 12px;
          position: relative;
          overflow: hidden;
        ">
          <div style="
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, #28a745, #20c997);
          "></div>
          <p style="
            color: #155724;
            font-size: 10px;
            margin: 0;
            font-family: 'Poppins', sans-serif;
            font-weight: 600;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
          ">
            <span style="
              width: 6px;
              height: 6px;
              background: linear-gradient(135deg, #28a745, #20c997);
              border-radius: 50%;
              display: inline-block;
              animation: pulse 2s infinite;
              box-shadow: 0 0 4px rgba(40, 167, 69, 0.25);
            "></span>
            <span>No other participants in room yet</span>
          </p>
        </div>
      `;
      
      document.body.appendChild(floatingInfo);
      
    } catch (error) {
    }
  };
  
  // Apply custom styles for join button and fonts
  const applyKauveryStyles = () => {
    try {
      // Add Poppins font
      const fontLink = document.createElement('link');
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);

      // Add custom CSS for join button gradient
      const style = document.createElement('style');
      style.textContent = `
        /* Force remove all borders from pre-join view */
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-radius: 0 !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
        }
        
        /* Target any element with a dark background (video preview) */
        .zego-prejoin-view [style*="background"],
        .zego-prejoin-container [style*="background"],
        .zego-prejoin [style*="background"] {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-radius: 0 !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
        }
        
        /* Override any existing border styles */
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-radius: 0 !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
        }
        
        /* Keep border reset scoped to pre-join only */
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-radius: 0 !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
        }
        
        /* Ensure header shadow is preserved */
        header, [data-testid="header"], .header {
          box-shadow: 0 8px 25px rgba(150, 32, 103, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          filter: drop-shadow(0 8px 25px rgba(150, 32, 103, 0.3)) !important;
        }
        
        /* Global fitting styles */
        /* Global fitting styles */
        html, body {
          max-width: 100vw !important;
          overflow-x: hidden !important;
          overflow-y: auto !important;
          box-sizing: border-box !important;
        }

        * {
          box-sizing: border-box !important;
        }

        /* Do NOT hide the Join button – keep it visible so user can enter the room; style it below */
        
        .zego-prejoin-view button:hover,
        .zego-prejoin-view .zego-button:hover,
        .zego-prejoin-view [class*="button"]:hover,
        .zego-prejoin-view [class*="join"]:hover,
        .zego-prejoin-view button[type="button"]:hover,
        .zego-prejoin-view .zego-ui-button:hover,
        .zego-prejoin-view .zego-btn:hover,
        .zego-prejoin-view .join-btn:hover,
        .zego-prejoin-view .start-btn:hover,
        .zego-prejoin-view .primary-btn:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 12px rgba(162, 50, 147, 0.3) !important;
          background: linear-gradient(135deg, #B23DA4, #FF3D78) !important;
        }
        
        /* Target all elements in pre-join view for Poppins font */
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          font-family: 'Poppins', sans-serif !important;
        }
        
        /* Specific targeting for titles and content */
        .zego-prejoin-view h1,
        .zego-prejoin-view h2,
        .zego-prejoin-view h3,
        .zego-prejoin-view .title,
        .zego-prejoin-view .header,
        .zego-prejoin-title {
          font-family: 'Poppins', sans-serif !important;
          font-weight: 600 !important;
        }
        
        .zego-prejoin-view p,
        .zego-prejoin-view span,
        .zego-prejoin-view div,
                  .zego-prejoin-content {
            font-family: 'Poppins', sans-serif !important;
          }
        
        /* Force override for any button-like elements */
        .zego-prejoin-view [class*="btn"],
        .zego-prejoin-view [class*="Button"],
        .zego-prejoin-view [class*="button"] {
          background: linear-gradient(135deg, #A23293, #EE2D67) !important;
          border: none !important;
          color: white !important;
          font-family: 'Poppins', sans-serif !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          padding: 12px 24px !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
        }

        /* Ensure Zego elements fit properly - only for pre-join view */
        .zego-prejoin-view,
        .zego-prejoin-container,
        .zego-prejoin {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
          overflow: hidden !important;
          box-sizing: border-box !important;
          border-radius: 0 !important;
          border: none !important;
          box-shadow: none !important;
          margin: 0 !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          position: relative !important;
        }

        /* Remove aggressive styling for video view - let Zego handle its own interface */
        .zego-video-view,
        .zego-video-container,
        .zego-ui {
          /* Remove all forced styling to let Zego display normally */
        }
        
        /* Ensure pre-join view middle area is white */
        .zego-prejoin-view,
        .zego-prejoin-container,
        .zego-prejoin {
          background: #ffffff !important;
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          outline: none !important;
        }
        
        /* Simple border removal for pre-join view */
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          outline: none !important;
        }
        
        /* Target the specific video preview container */
        .zego-prejoin-view [style*="background-color"],
        .zego-prejoin-container [style*="background-color"],
        .zego-prejoin [style*="background-color"],
        .zego-prejoin-view [style*="background"],
        .zego-prejoin-container [style*="background"],
        .zego-prejoin [style*="background"] {
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          outline: none !important;
        }
        
        /* Nuclear option - remove borders from everything in pre-join view */
        .zego-prejoin-view,
        .zego-prejoin-container,
        .zego-prejoin,
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          border: none !important;
          border-width: 0 !important;
          border-style: none !important;
          border-color: transparent !important;
          border-radius: 0 !important;
          outline: none !important;
          outline-width: 0 !important;
          box-shadow: none !important;
        }
        /* Restore card styling for Doctor/Patient and room status (previous design – second image) */
        .zego-prejoin-view .kauvery-participant-info,
        .zego-prejoin-container .kauvery-participant-info,
        .kauvery-participant-info {
          border-radius: 20px !important;
          box-shadow: 0 8px 25px rgba(162, 50, 147, 0.15) !important;
          border: 2px solid rgba(162, 50, 147, 0.2) !important;
        }
        .zego-prejoin-view .kauvery-participant-info > div > div:first-child > div,
        .zego-prejoin-container .kauvery-participant-info > div > div:first-child > div {
          border-radius: 25px !important;
          box-shadow: 0 4px 15px rgba(162, 50, 147, 0.1) !important;
          border: 2px solid rgba(162, 50, 147, 0.2) !important;
        }
        .kauvery-participant-info #room-status,
        .kauvery-participant-info [id="room-status"] {
          border-radius: 20px !important;
          box-shadow: 0 4px 15px rgba(40, 167, 69, 0.15) !important;
          border: 2px solid rgba(40, 167, 69, 0.3) !important;
        }

        /* Responsive design for different screen sizes - only for pre-join view */
        @media (min-width: 1201px) {
          /* Desktop - Single column layout */
          .zego-prejoin-view,
          .zego-prejoin-container,
          .zego-prejoin {
            width: 100% !important;
            height: 100% !important;
            position: relative !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .kauvery-participant-info,
          .kauvery-floating-info {
            position: relative !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            z-index: 1 !important;
            transform: none !important;
            align-self: center !important;
            justify-self: center !important;
          }
        }

        @media (max-width: 1200px) and (min-width: 769px) {
          /* Laptop - Single column layout */
          .zego-prejoin-view,
          .zego-prejoin-container,
          .zego-prejoin {
            width: 100% !important;
            height: 100% !important;
            position: relative !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .kauvery-participant-info,
          .kauvery-floating-info {
            position: relative !important;
            margin: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            z-index: 1 !important;
            align-self: center !important;
            justify-self: center !important;
          }
        }

        @media (max-width: 768px) {
          /* Tablet and Mobile - Stacked layout */
          .zego-prejoin-view,
          .zego-prejoin-container,
          .zego-prejoin {
            width: 100% !important;
            height: 100% !important;
            position: relative !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            grid-column: 1 !important;
            grid-row: 1 !important;
          }
          
          .kauvery-participant-info,
          .kauvery-floating-info {
            /* Mobile-only: hide doctor/patient detail cards as requested */
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            pointer-events: none !important;
          }

          /* Keep Join button and details card in clean vertical flow on mobile */
          .zego-prejoin-view button,
          .zego-prejoin-view [class*="button"],
          .zego-prejoin-view [class*="join"] {
            width: 100% !important;
            max-width: 360px !important;
            margin: 12px auto 10px auto !important;
            display: block !important;
          }

          .kauvery-participant-info {
            display: none !important;
          }

          .kauvery-participant-info > div > div:first-child {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          
          .kauvery-participant-info h3,
          .kauvery-floating-info h3 {
            font-size: 18px !important;
          }
          
          .kauvery-participant-info .appointment-details,
          .kauvery-floating-info .appointment-details {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
        }

        @media (max-width: 480px) {
          /* Small mobile devices */
          .zego-prejoin-view,
          .zego-prejoin-container,
          .zego-prejoin {
            height: 50% !important;
            margin-bottom: 10px !important;
          }
          
          .kauvery-participant-info,
          .kauvery-floating-info {
            width: 98% !important;
            margin: 0 auto 10px auto !important;
            font-size: 12px !important;
            padding: 10px !important;
          }
          
          .kauvery-participant-info h3,
          .kauvery-floating-info h3 {
            font-size: 16px !important;
          }
        }

        /* Hide patient name input fields - only in pre-join view */
        .zego-prejoin-view input[type="text"],
        .zego-prejoin-view input[placeholder*="name"],
        .zego-prejoin-view input[placeholder*="Name"],
        .zego-prejoin-view [class*="input"],
        .zego-prejoin-view [class*="textbox"] {
          display: none !important;
        }

        /* Hide labels for patient name - only in pre-join view */
        .zego-prejoin-view label,
        .zego-prejoin-view [class*="label"] {
          display: none !important;
        }

        /* Keep "Welcome to Kauvery Hospital" and title visible (previous design – second image) */

        /* More aggressive title color override - only for pre-join view */
        .zego-prejoin-view *,
        .zego-prejoin-container *,
        .zego-prejoin * {
          color: #962067 !important;
        }

        /* Ensure Zego pre-join view middle area is white */
        .zego-prejoin-view,
        .zego-prejoin-container,
        .zego-prejoin {
          background: #ffffff !important;
          position: relative !important;
          border: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          outline: none !important;
        }

        /* Remove any duplicate participant info */
        .kauvery-floating-info {
          display: none !important;
        }

        /* Ensure Doctor/Patient card and room status always visible (second image design) */
        .kauvery-participant-info {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: relative !important;
          margin: 20px auto !important;
          width: 100% !important;
          max-width: 400px !important;
          z-index: 10 !important;
          transform: none !important;
          box-shadow: 0 4px 16px rgba(162, 50, 147, 0.15) !important;
          border-radius: 12px !important;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(248, 249, 250, 0.98)) !important;
          backdrop-filter: blur(8px) !important;
        }

        /* Specific targeting for any text containing "Welcome" */
        .zego-prejoin-view *:contains("Welcome"),
        .zego-prejoin-container *:contains("Welcome"),
        .zego-prejoin *:contains("Welcome") {
          color: #962067 !important;
        }

        /* Universal selector for all text in pre-join view */
        .zego-prejoin-view,
        .zego-prejoin-container,
        .zego-prejoin {
          color: #962067 !important;
        }

        /* Target any element with text content */
        .zego-prejoin-view div,
        .zego-prejoin-view span,
        .zego-prejoin-view p {
          color: #962067 !important;
        }

        /* Hide only quit/leave confirmation screens, not toolbar or Join button */
        .zego-quit-view,
        .zego-quit-container,
        [class*="quit-view"],
        [class*="quitView"],
        [class*="leave-view"],
        [class*="leaveView"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
        
        /* Ensure Zego's join button is visible and styled (Kauvery design) */
        .zego-prejoin-view button,
        .zego-prejoin-view [class*="button"],
        .zego-prejoin-view [class*="join"] {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          background: linear-gradient(135deg, #A23293, #EE2D67) !important;
          color: white !important;
          border: none !important;
          border-radius: 14px !important;
          padding: 18px 28px !important;
          font-family: 'Poppins', sans-serif !important;
          font-weight: 600 !important;
          font-size: 15px !important;
          cursor: pointer !important;
          transition: all 0.3s ease !important;
          box-shadow: 0 5px 18px rgba(162, 50, 147, 0.25) !important;
          width: 100% !important;
          max-width: 400px !important;
          margin: 20px auto 0 auto !important;
        }
        
        .zego-prejoin-view button:hover,
        .zego-prejoin-view [class*="button"]:hover,
        .zego-prejoin-view [class*="join"]:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 25px rgba(162, 50, 147, 0.35) !important;
          background: linear-gradient(135deg, #B23DA4, #FF3D78) !important;
        }

        /* Hide any Zego elements that appear after call ends - only in pre-join view */
        .zego-ui[data-ended="true"],
        .zego-ui[data-left="true"],
        .zego-ui[data-quit="true"] {
          display: none !important;
        }

        /* Ensure our custom quit page takes precedence */
        .kauvery-call-ended {
          z-index: 9999 !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          background: #ffffff;
        }
      `;
      document.head.appendChild(style);
      
      // Add MutationObserver to ensure styles are applied to dynamically created elements
      observerRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Simple border removal for pre-join view elements – do NOT strip our Doctor/Patient card
                const newElements = node.querySelectorAll ? node.querySelectorAll('*') : [node];
                newElements.forEach(element => {
                  if (element.closest && element.closest('.kauvery-participant-info')) return;
                  if (element.closest('.zego-prejoin-view') || element.closest('.zego-prejoin-container') || element.closest('.zego-prejoin')) {
                    element.style.border = 'none';
                    element.style.borderRadius = '0';
                    element.style.boxShadow = 'none';
                    element.style.outline = 'none';
                  }
                });
                
                // Check if any buttons were added
                const buttons = node.querySelectorAll ? node.querySelectorAll('button, [class*="button"], [class*="btn"], [class*="join"]') : [];
                buttons.forEach(button => {
                  if (button.textContent && (button.textContent.toLowerCase().includes('join') || 
                      button.textContent.toLowerCase().includes('start') || 
                      button.textContent.toLowerCase().includes('enter'))) {
                    
                    // Update button text
                    button.textContent = 'Join Teleconsultation';
                    
                    // Apply gradient styles
                    button.style.background = 'linear-gradient(135deg, #A23293, #EE2D67)';
                    button.style.border = 'none';
                    button.style.color = 'white';
                    button.style.fontFamily = "'Poppins', sans-serif";
                    button.style.fontWeight = '600';
                    button.style.borderRadius = '8px';
                    button.style.padding = '12px 24px';
                    button.style.cursor = 'pointer';
                    button.style.transition = 'all 0.3s ease';
                    button.style.boxShadow = '0 2px 8px rgba(162, 50, 147, 0.2)';
                    
                    // Add hover effect
                    button.addEventListener('mouseenter', () => {
                      button.style.transform = 'translateY(-2px)';
                      button.style.boxShadow = '0 4px 12px rgba(162, 50, 147, 0.3)';
                      button.style.background = 'linear-gradient(135deg, #B23DA4, #FF3D78)';
                    });
                    
                    button.addEventListener('mouseleave', () => {
                      button.style.transform = 'translateY(0)';
                      button.style.boxShadow = '0 2px 8px rgba(162, 50, 147, 0.2)';
                      button.style.background = 'linear-gradient(135deg, #A23293, #EE2D67)';
                    });
                    
                                        // Only trigger participant info customization if not already done
        if (!document.querySelector('.kauvery-participant-info')) {
          setTimeout(() => {
            customizePreJoinView();
          }, 100);
        }
                  }
                });

                // Check if any input fields were added and hide them
                const inputFields = node.querySelectorAll ? node.querySelectorAll('input[type="text"], input[placeholder*="name"], input[placeholder*="Name"], [class*="input"], [class*="textbox"]') : [];
                inputFields.forEach(input => {
                  input.style.display = 'none';
                  
                  // Also hide the parent container if it's just for the input
                  const parent = input.parentElement;
                  if (parent && (parent.className.includes('input') || parent.className.includes('textbox') || parent.className.includes('field'))) {
                    parent.style.display = 'none';
                  }
                });

                // Check if any labels were added and hide them
                const labels = node.querySelectorAll ? node.querySelectorAll('label, [class*="label"]') : [];
                labels.forEach(label => {
                  if (label.textContent && (
                    label.textContent.toLowerCase().includes('name') ||
                    label.textContent.toLowerCase().includes('username') ||
                    label.textContent.toLowerCase().includes('enter')
                  )) {
                    label.style.display = 'none';
                  }
                });

                // Check if any titles were added and change their color
                const titles = node.querySelectorAll ? node.querySelectorAll('h1, h2, h3, [class*="title"], [class*="header"], [class*="welcome"]') : [];
                titles.forEach(title => {
                  if (title.textContent && title.textContent.toLowerCase().includes('welcome')) {
                    title.style.color = '#962067';
                    title.style.fontFamily = "'Poppins', sans-serif";
                    title.style.fontWeight = '600';
                    title.style.fontSize = '24px';
                  }
                });

                // More aggressive title color application
                const welcomeElements = node.querySelectorAll ? node.querySelectorAll('*') : [];
                welcomeElements.forEach(element => {
                  if (element.textContent && element.textContent.toLowerCase().includes('welcome')) {
                    element.style.color = '#962067';
                    element.style.fontFamily = "'Poppins', sans-serif";
                    element.style.fontWeight = '600';
                    element.style.fontSize = '24px';
                  }
                });

                // Also check the entire document for welcome text
                setTimeout(() => {
                  const allWelcomeElements = document.querySelectorAll('*');
                  allWelcomeElements.forEach(element => {
                    if (element.textContent && element.textContent.toLowerCase().includes('welcome') && 
                        element.closest('.zego-prejoin-view')) {
                      element.style.color = '#962067';
                      element.style.fontFamily = "'Poppins', sans-serif";
                      element.style.fontWeight = '600';
                      element.style.fontSize = '24px';
                    }
                  });
                }, 500);

                // Hide only full-screen quit/leave views (do not hide in-room controls)
                const quitElements = node.querySelectorAll ? node.querySelectorAll('[class*="quit-view"], [class*="quitView"], [class*="leave-view"], [class*="leaveView"]') : [];
                quitElements.forEach(element => {
                  element.style.display = 'none';
                  element.style.visibility = 'hidden';
                  element.style.opacity = '0';
                  element.style.pointerEvents = 'none';
                });

                // Rewire only explicit leave/end text buttons (avoid icon buttons for mic/video/chat)
                const endCallButtons = node.querySelectorAll ? node.querySelectorAll('button, [class*="button"], [class*="btn"]') : [];
                endCallButtons.forEach(button => {
                  if (button.textContent && (
                    button.textContent.toLowerCase().includes('end call') ||
                    button.textContent.toLowerCase().includes('leave') ||
                    button.textContent.toLowerCase().includes('quit') ||
                    button.textContent.toLowerCase().includes('hang up') ||
                    button.textContent.toLowerCase().includes('end') ||
                    button.textContent.toLowerCase().includes('exit')
                  )) {
                    button.onclick = (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      handleEndCall();
                      return false;
                    };
                    button.addEventListener('click', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.stopImmediatePropagation();
                      handleEndCall();
                      return false;
                    }, true);
                  }
                });

                // Hide only leave confirmation popups; keep chat/toolbar overlays
                if (node.className && (
                  node.className.toLowerCase().includes('popup') ||
                  node.className.toLowerCase().includes('modal') ||
                  node.className.toLowerCase().includes('dialog') ||
                  node.className.toLowerCase().includes('overlay')
                )) {
                  const popupText = (node.textContent || '').toLowerCase();
                  if (popupText.includes('leave the room') || popupText.includes('are you sure')) {
                    node.style.display = 'none';
                    node.style.visibility = 'hidden';
                    node.style.opacity = '0';
                    node.style.pointerEvents = 'none';
                  }
                }

                // Check for text that indicates call ended
                if (node.textContent && (
                  node.textContent.toLowerCase().includes('you have left the room') ||
                  node.textContent.toLowerCase().includes('call ended') ||
                  node.textContent.toLowerCase().includes('consultation ended') ||
                  node.textContent.toLowerCase().includes('return to home')
                )) {
                  node.style.display = 'none';
                  node.style.visibility = 'hidden';
                  node.style.opacity = '0';
                  node.style.pointerEvents = 'none';
                  
                  // Trigger our custom call ended page
                  if (!callEnded) {
                    setCallEnded(true);
                  }
                }
              }
            });
          }
        });
      });
      
      // Start observing the document body for changes
      observerRef.current.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Function to force update title color
      const forceUpdateTitleColor = () => {
        const titleElements = document.querySelectorAll('*');
        titleElements.forEach(element => {
          if (element.textContent && element.textContent.toLowerCase().includes('welcome') && 
              element.closest('.zego-prejoin-view')) {
            element.style.color = '#962067';
            element.style.fontFamily = "'Poppins', sans-serif";
            element.style.fontWeight = '600';
            element.style.fontSize = '24px';
          }
        });
      };

      // Periodically check and update title color
      setInterval(forceUpdateTitleColor, 1000);
      
      // Periodically hide only quit/leave confirmation screens (not Join button or participant card)
      setInterval(() => {
        const quitViewOnly = document.querySelectorAll('[class*="quit-view"], [class*="quitView"], [class*="leave-view"], [class*="leaveView"]');
        quitViewOnly.forEach(element => {
          if (element.style.display !== 'none') {
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
          }
        });
        const popupElements = document.querySelectorAll('[class*="popup"], [class*="modal"], [class*="dialog"], [class*="overlay"]');
        popupElements.forEach(element => {
          if (element.classList.contains('kauvery-confirm-button')) return;
          const text = (element.textContent || '').toLowerCase();
          if ((text.includes('leave the room') || text.includes('are you sure')) && element.style.display !== 'none') {
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
          }
        });
        
        // Check for call ended text
        const callEndedElements = document.querySelectorAll('*');
        callEndedElements.forEach(element => {
          if (element.textContent && (
            element.textContent.toLowerCase().includes('you have left the room') ||
            element.textContent.toLowerCase().includes('call ended') ||
            element.textContent.toLowerCase().includes('consultation ended') ||
            element.textContent.toLowerCase().includes('return to home')
          )) {
            element.style.display = 'none';
            element.style.visibility = 'hidden';
            element.style.opacity = '0';
            element.style.pointerEvents = 'none';
            
            // Trigger our custom call ended page
            if (!callEnded) {
              setCallEnded(true);
            }
          }
        });
      }, 500);
      
      // Simple continuous border removal for pre-join view
      setInterval(() => {
        const prejoinElements = document.querySelectorAll('.zego-prejoin-view *, .zego-prejoin-container *, .zego-prejoin *');
        prejoinElements.forEach(element => {
          if (element.closest && element.closest('.kauvery-participant-info')) return;
          element.style.border = 'none';
          element.style.borderRadius = '0';
          element.style.boxShadow = 'none';
          element.style.outline = 'none';
        });
      }, 100);
      
              // Make functions globally available for debugging
        window.customizePreJoinView = customizePreJoinView;
        window.forceUpdateTitleColor = forceUpdateTitleColor;
        window.handleEndCall = handleEndCall;
      
    } catch (error) {
    }
  };

  // Initialize Zego with Kauvery styling (called directly on page load)
  const initializeZego = async () => {
    try {
      // Enhanced prevention of multiple initializations
      /* Do NOT check document for [class*="zego"] – our container has class "zego-video-container" and that blocks first load */
      if (zegoInitialized || zegoInstanceRef.current) {
        return;
      }

      // Safety check for appointment data
      if (!appointmentData || !appointmentData.roomID || !appointmentData.userid || !appointmentData.username) {
       
        setTimeout(initializeZego, 500);
        return;
      }

      // Wait for container to be ready
      if (!zegoContainerRef.current) {
        setTimeout(initializeZego, 500);
        return;
      }

      setInitializationError(null);

      // Token from server only; no credentials in client (VAPT)
      const { token, appId } = await fetchZegoKitToken(
        appointmentData.roomID,
        appointmentData.userid,
        appointmentData.username || 'Patient'
      );
      const numericAppID = parseInt(appId, 10);
      if (isNaN(numericAppID)) {
        throw new Error('Invalid video session configuration.');
      }

      let ZegoUIKitPrebuilt;
      try {
        const zegoModule = await import('@zegocloud/zego-uikit-prebuilt');
        ZegoUIKitPrebuilt = zegoModule.ZegoUIKitPrebuilt || zegoModule.default || zegoModule;
      } catch (importError) {
        throw new Error(`Failed to import ZegoUIKitPrebuilt: ${importError.message}`);
      }

      if (!ZegoUIKitPrebuilt) {
        throw new Error('ZegoUIKitPrebuilt is undefined after import');
      }

      let kitToken;
      if (ZegoUIKitPrebuilt.generateKitTokenForProduction) {
        kitToken = ZegoUIKitPrebuilt.generateKitTokenForProduction(
          numericAppID,
          token,
          appointmentData.roomID,
          appointmentData.userid,
          appointmentData.username || 'Patient'
        );
      } else {
        throw new Error('Video session requires generateKitTokenForProduction.');
      }

      if (!ZegoUIKitPrebuilt.create) {
        throw new Error('ZegoUIKitPrebuilt.create method is not available');
      }

      let zp;
      try {
        zp = ZegoUIKitPrebuilt.create(kitToken);
      } catch (createError) {
        throw new Error(`Failed to create Zego instance: ${createError.message}`);
      }

      if (!zp) {
        throw new Error('Failed to create Zego instance - zp is undefined');
      }
      
      if (!zp.joinRoom) {
        throw new Error('Zego instance does not have joinRoom method');
      }
      
      zegoInstanceRef.current = zp;

      // Join room with pre-join screen (original Zego interface)
      
      try {
        await zp.joinRoom({
          container: zegoContainerRef.current,
          scenario: {
            mode: ZegoUIKitPrebuilt.VideoConference,
          },
          showPreJoinView: true,
          autoHideFooter: false, // Keep bottom toolbar visible on mobile
          showMyMicrophoneToggleButton: true,
          showMyCameraToggleButton: true,
          showTextChat: true,
          showScreenSharingButton: true,
          preJoinViewConfig: {
            title: `Welcome to Kauvery Hospital`,
            titleStyle: {
              color: '#962067',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: '600',
              fontSize: '20px'
            }
          },
          onJoinRoom: () => {
            // Store appointment data FIRST before any video call events
            if (appointmentData && !appointmentStored) {
              const appointmentToStore = {
                app_no: appointmentData.app_no || globalParsedParams.app_no,
                username: appointmentData.username || globalParsedParams.username,
                userid: appointmentData.userid || globalParsedParams.userid,
                doctorname: appointmentData.doctorname || globalParsedParams.doctorname,
                speciality: appointmentData.speciality || globalParsedParams.speciality,
                appointment_date: appointmentData.appointment_date || globalParsedParams.appointment_date,
                appointment_time: appointmentData.appointment_time || globalParsedParams.appointment_time,
                // appointment_date: "15/09/2025 00:00:00",
                // appointment_time: "15:30",
                roomID: appointmentData.app_no // Temporary room ID (appointment number)
              };
              
              // Store appointment first, then handle video call events
              AppointmentService.storeAppointment(appointmentToStore)
                .then(result => {
                  setAppointmentStored(true);
                  
                  // Store appointment ID for future use
                  if (result.appointment_id) {
                    AppointmentService.setAppointmentId(result.appointment_id);
                    
                    // Update room ID to use just appointment ID
                    const newRoomId = result.appointment_id.toString();
                    setAppointmentData(prev => ({
                      ...prev,
                      // roomId: newRoomId
                    }));
                  }
                  
                  // NOW track connection event after appointment is stored
                  if (!connectEventSent) {
                    handleConnectionEvent('connected', { 
                      roomID: result.appointment_id.toString(),
                      join_time: new Date().toISOString()
                    });
                    setConnectEventSent(true);
                  }
                  
                  // Start call session after appointment is stored
                  AppointmentService.startCallSession({
                    appointment_id: parseInt(result.appointment_id),
                    roomID: result.appointment_id.toString(),
                    user_id: appointmentData.userid,
                    username: appointmentData.username
                  }).catch(() => {});
                })
                .catch(err => {
                });
            } else if (appointmentStored) {
              // If appointment is already stored, just track the connection event
              if (!connectEventSent) {
                handleConnectionEvent('connected', { 
                  roomID: appointmentData.roomID,
                  join_time: new Date().toISOString()
                });
                setConnectEventSent(true);
              }
            }
            
            // Use longer timeout to ensure pre-join to video transition is complete
            setTimeout(() => {
              setZegoInitialized(true);
            }, 500);
          },
          onLeaveRoom: () => {
            // Track connection event
            handleConnectionEvent('disconnected', { 
              roomID: appointmentData.roomID,
              leave_time: new Date().toISOString()
            });
            
            // Track video call leave event (existing code)
            const appointmentId = AppointmentService.getAppointmentId();
            if (appointmentId && appointmentData) {
              // End call session
              AppointmentService.endCallSession({
                appointment_id: parseInt(appointmentId)
              }).catch(() => {});
              
              // Store video call event
              AppointmentService.storeVideoCallEvent({
                appointment_id: parseInt(appointmentId),
                event_type: 'leave_room',
                roomID: appointmentData.roomID,
                user_id: appointmentData.userid,
                username: appointmentData.username,
                event_data: {
                  timestamp: new Date().toISOString(),
                  roomID: appointmentData.roomID,
                  user_id: appointmentData.userid,
                  username: appointmentData.username
                }
              }).catch(() => {});
            }
            
            setZegoInitialized(false);
            setCallEnded(true);
            zegoInstanceRef.current = null;
            // Redirect to Kauvery Hospital and clear session after call ends
            setTimeout(completeSessionAndRedirect, 500);
          },
          onError: (error) => {
            setInitializationError(error);
          },
                  onPreJoinViewReady: () => {
          // Add Doctor/Patient card and "No one else in the room" (second image design)
          setTimeout(() => customizePreJoinView(), 300);
          setTimeout(() => {
            if (!document.querySelector('.kauvery-participant-info')) customizePreJoinView();
          }, 1200);
        },
        onJoinRoomSuccess: () => {
          
        },
        // Additional event listeners for connection tracking
        onUserUpdate: (updateType, userList) => {          
          // Update participant count based on the actual user list
          setParticipantCount(userList.length);
          
          // Update status display immediately
          setTimeout(() => {
            updateStatusDisplay();
          }, 100);
          
          // Track the event
          if (updateType === 'ADD') {
            handleConnectionEvent('user_joined', { 
              room_id: appointmentData.roomId,
              user_count: userList.length,
              users: userList.map(u => ({ id: u.userID, name: u.userName }))
            });
          } else if (updateType === 'DELETE') {
            handleConnectionEvent('user_left', { 
              room_id: appointmentData.roomId,
              user_count: userList.length,
              users: userList.map(u => ({ id: u.userID, name: u.userName }))
            });
          }
          
          // Check if both patient and doctor are in the room (2 or more participants)
          if (userList.length >= 2 && !appointmentStored && appointmentData) {
            // Store appointment data when both participants are connected
            const appointmentToStore = {
              app_no: appointmentData.app_no,
              username: appointmentData.username,
              userid: appointmentData.userid,
              roomID: appointmentData.roomID,
              doctorname: appointmentData.doctorname,
              speciality: appointmentData.speciality,
              appointment_date: "15/09/2025 00:00:00",
              appointment_time: "15:30"
            };
            
            AppointmentService.storeAppointment(appointmentToStore)
              .then(result => {
                setAppointmentStored(true);
                if (result.appointment_id) {
                  AppointmentService.setAppointmentId(result.appointment_id);
                }
              })
              .catch(err => {
              });
          }
        },
        
        onRoomStateUpdate: (roomState) => {
          
          if (roomState.reason === 'Reconnecting' || roomState.reason === 'RECONNECTING') {
            handleConnectionEvent('reconnecting', { 
              room_state: roomState,
              reconnect_time: new Date().toISOString()
            });
          } else if (roomState.reason === 'Reconnected' || roomState.reason === 'RECONNECTED') {
            handleConnectionEvent('reconnected', { 
              room_state: roomState,
              reconnected_time: new Date().toISOString()
            });
          }
        },
        
        onNetworkQuality: (quality) => {
          trackVideoCallEvent('network_quality_update', { 
            quality: quality,
            timestamp: new Date().toISOString()
          });
        }
        });
    } catch (joinError) {
      setInitializationError(joinError.message || 'Failed to join room');
      throw joinError;
    }
    
    // Fallback: Set initialized state if onJoinRoom doesn't trigger
    setTimeout(() => {
      if (!zegoInitialized && zegoInstanceRef.current) {
        setZegoInitialized(true);
      }
    }, 2000);
    
    return true;
    } catch (error) {
      setInitializationError(error.message || 'Failed to initialize video consultation');
      setZegoInitialized(false);
      
      // Show user-friendly error message
      showNotification('Unable to connect to video consultation. Please refresh the page.', 'error');
      
      // Reset after 5 seconds to allow retry
      setTimeout(() => {
        setInitializationError(null);
        zegoInstanceRef.current = null;
      }, 5000);
      
      throw error;
    }
  };

  // Styles object
  const styles = {
    body: {
      fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: theme.pageBg,
      color: colors.kauveryDarkGrey,
      overflow: 'hidden',
      height: '100vh',
      maxHeight: '100vh',
      margin: 0,
      padding: 0
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '70px', // Increased header height
              background: colors.white, // White header background
        borderBottom: `3px solid ${colors.kauveryPurple}`, // Purple border
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.15)', // Much more prominent shadow
        filter: 'drop-shadow(0 8px 25px rgba(0, 0, 0, 0.15))', // Additional drop-shadow for better browser support
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${spacing.sm} ${spacing.lg}`,
      // Responsive header
      '@media (max-width: 768px)': {
        height: '60px',
        padding: `${spacing.xs} ${spacing.md}`,
        boxShadow: '0 6px 20px rgba(150, 32, 103, 0.25), 0 3px 10px rgba(0, 0, 0, 0.12)'
      },
      '@media (max-width: 480px)': {
        height: '55px',
        padding: `${spacing.xs} ${spacing.sm}`,
        boxShadow: '0 4px 15px rgba(150, 32, 103, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)'
      }
    },
    brandingSection: {
      display: 'flex',
      alignItems: 'center'
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.md
    },
    yearsBadge: {
      width: '100px', // Reduced logo size
      height: '48px', // Reduced logo height
      background: 'transparent',
      borderRadius: radius.md,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      // Responsive logo
      '@media (max-width: 768px)': {
        width: '80px',
        height: '40px'
      },
      '@media (max-width: 480px)': {
        width: '70px',
        height: '35px'
      }
    },
    hospitalInfo: {
      display: 'flex',
      flexDirection: 'column'
    },
    hospitalName: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '18px', // Reduced font size
      fontWeight: 600,
      color: colors.kauveryPurple, // Keep purple for brand consistency
      margin: 0,
      lineHeight: 1.2,
      // Responsive text
      '@media (max-width: 768px)': {
        fontSize: '16px'
      },
      '@media (max-width: 480px)': {
        fontSize: '14px'
      }
    },
    hospitalSubtitle: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '11px', // Reduced font size
      color: colors.kauveryDarkGrey,
      margin: 0,
      fontWeight: 500,
      // Responsive text
      '@media (max-width: 768px)': {
        fontSize: '10px'
      },
      '@media (max-width: 480px)': {
        fontSize: '9px'
      }
    },
    appointmentSection: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.xl
    },
    appointmentDetails: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: `${spacing.xs} ${spacing.md}`,
      padding: spacing.sm,
      background: colors.grey50,
      borderRadius: radius.lg,
      border: `2px solid ${colors.kauveryPink}`,
      minWidth: '350px'
    },
    appointmentItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    appointmentLabel: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '10px',
      color: colors.kauveryLightGrey,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    appointmentValue: {
      fontFamily: "'Poppins', sans-serif",
      fontSize: '12px',
      color: colors.kauveryDarkGrey,
      fontWeight: 600
    },
    statusBadge: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      background: `linear-gradient(135deg, ${colors.kauveryYellowOrange}, ${colors.kauveryPink})`,
      color: colors.white,
      padding: `${spacing.sm} ${spacing.md}`, // Reduced padding
      borderRadius: radius.xl,
      fontWeight: 600,
      fontSize: '13px', // Reduced font size
      boxShadow: shadows.md,
      // Responsive status badge
      '@media (max-width: 768px)': {
        padding: `${spacing.xs} ${spacing.sm}`,
        fontSize: '11px',
        gap: spacing.xs
      },
      '@media (max-width: 480px)': {
        padding: `${spacing.xs} ${spacing.xs}`,
        fontSize: '10px',
        gap: spacing.xs
      }
    },
    statusIcon: {
      fontSize: '16px'
    },
    statusText: {
      fontWeight: 700
    },
    notification: {
      position: 'fixed',
      top: spacing.lg,
      right: spacing.lg,
      background: colors.white,
      color: colors.kauveryDarkGrey,
      padding: `${spacing.md} ${spacing.lg}`,
      borderRadius: radius.lg,
      boxShadow: shadows.lg,
      zIndex: 100,
      transform: 'translateX(100%)',
      transition: 'transform 0.3s ease'
    }
  };

  // Access Denied Component
  const AccessDeniedPage = () => {
    
    return (
      <div style={{
        ...styles.body,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.xl
      }}>
      <div style={{
        background: colors.white,
        borderRadius: radius.xl,
        padding: spacing['2xl'],
        maxWidth: '500px',
        textAlign: 'center',
        boxShadow: shadows.lg,
        border: `3px solid ${colors.kauveryRed}`
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          background: `linear-gradient(135deg, ${colors.kauveryRed}, ${colors.kauveryPink})`,
          borderRadius: radius.full,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          fontSize: '32px'
        }}>
          ⚠️
        </div>
        
        <h1 style={{
          color: colors.kauveryRed,
          fontSize: '28px',
          fontWeight: 700,
          margin: '0 0 16px 0',
          fontFamily: "'Google Sans', sans-serif"
        }}>
          Access Denied
        </h1>
        
        <p style={{
          color: colors.kauveryDarkGrey,
          fontSize: '16px',
          lineHeight: 1.6,
          margin: '0 0 24px 0'
        }}>
          {decodingError || accessDeniedReason}
        </p>
        
        <div style={{
          background: colors.grey50,
          borderRadius: radius.lg,
          padding: spacing.lg,
          margin: '24px 0',
          border: `2px solid ${colors.grey200}`
        }}>
          <h3 style={{
            color: colors.kauveryPurple,
            fontSize: '18px',
            fontWeight: 600,
            margin: '0 0 12px 0'
          }}>
            Required Parameters:
          </h3>
          <ul style={{
            textAlign: 'left',
            color: colors.kauveryDarkGrey,
            fontSize: '14px',
            lineHeight: 1.8,
            margin: 0,
            paddingLeft: '20px'
          }}>
            <li><strong>id</strong> - Encoded Appointment ID containing all parameters</li>
          </ul>
          
          <div style={{
            background: colors.grey100,
            borderRadius: radius.md,
            padding: spacing.md,
            marginTop: spacing.md,
            border: `1px solid ${colors.grey300}`
          }}>
            <h4 style={{
              color: colors.kauveryPurple,
              fontSize: '14px',
              fontWeight: 600,
              margin: '0 0 8px 0'
            }}>
              Example URL:
            </h4>
            <p style={{
              color: colors.kauveryDarkGrey,
              fontSize: '12px',
              margin: 0,
              fontFamily: 'monospace',
              wordBreak: 'break-all'
            }}>
              {/* http://localhost:3000/?id=ODFlvLi0k4Ahvs6YIHnKCbJ//F1frN/vbVq+1c55QOZ1oa3keYEEZjCHHyvID7X5jfNNotg52mwz1TKIzOGJRw== */}
            </p>
          </div>
        </div>
        
        <button 
          onClick={() => window.history.back()}
          style={{
            background: `linear-gradient(135deg, ${colors.kauveryPurple}, ${colors.kauveryViolet})`,
            color: colors.white,
            border: 'none',
            borderRadius: radius.lg,
            padding: `${spacing.md} ${spacing.xl}`,
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            marginTop: spacing.lg
          }}
        >
          🔙 Go Back
        </button>
      </div>
    </div>
    );
  };

  // Health Packages Page Component
  const HealthPackagesPage = () => {
    const [packages, setPackages] = useState(healthPackages);

    const togglePackage = (packageId) => {
      setPackages(prevPackages => 
        prevPackages.map(pkg => 
          pkg.id === packageId 
            ? { ...pkg, expanded: !pkg.expanded }
            : pkg
        )
      );
    };

    return (
      <div style={{
        ...styles.body,
        padding: `${80}px ${spacing.xl} ${90}px ${spacing.xl}`,
        background: theme.pageBg,
        boxSizing: 'border-box',
        overflowY: 'auto'
      }}>
        <ConsultationHeader />

        {/* Main Content */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          paddingTop: spacing.xl
        }}>
          <h1 style={{
            color: colors.kauveryPurple,
            fontSize: '32px',
            fontWeight: 700,
            textAlign: 'center',
            margin: '0 0 40px 0',
            fontFamily: "'Poppins', sans-serif"
          }}>
            Choose Your Health Check Package
          </h1>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: spacing.lg,
            marginBottom: spacing.xl
          }}>
            {packages.map((pkg) => (
              <div key={pkg.id} style={{
                background: colors.white,
                borderRadius: radius.lg,
                border: `2px solid ${pkg.expanded ? colors.kauveryPink : colors.grey200}`,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                boxShadow: pkg.expanded ? shadows.md : shadows.sm
              }}>
                {/* Package Header */}
                <div 
                  onClick={() => togglePackage(pkg.id)}
                  style={{
                    padding: spacing.lg,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: pkg.expanded ? colors.grey50 : colors.white,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.md
                  }}>
                    <span style={{
                      fontSize: '24px',
                      width: '40px',
                      textAlign: 'center'
                    }}>
                      {pkg.icon}
                    </span>
                    <h3 style={{
                      color: colors.kauveryPurple,
                      fontSize: '18px',
                      fontWeight: 600,
                      margin: 0,
                      fontFamily: "'Poppins', sans-serif"
                    }}>
                      {pkg.title}
                    </h3>
                  </div>
                  <span style={{
                    fontSize: '20px',
                    color: colors.kauveryPurple,
                    transition: 'transform 0.3s ease',
                    transform: pkg.expanded ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    ▼
                  </span>
                </div>

                {/* Package Details */}
                {pkg.expanded && (
                  <div style={{
                    padding: `0 ${spacing.lg} ${spacing.lg} ${spacing.lg}`,
                    borderTop: `1px solid ${colors.grey200}`,
                    background: colors.grey50
                  }}>
                    <h4 style={{
                      color: colors.kauveryPurple,
                      fontSize: '16px',
                      fontWeight: 600,
                      margin: '0 0 12px 0'
                    }}>
                      Included Services:
                    </h4>
                    <ul style={{
                      margin: 0,
                      paddingLeft: spacing.lg,
                      color: colors.kauveryDarkGrey,
                      fontSize: '14px',
                      lineHeight: 1.8
                    }}>
                      {pkg.services.map((service, index) => (
                        <li key={index} style={{
                          marginBottom: spacing.xs,
                          position: 'relative'
                        }}>
                          <span style={{
                            color: colors.kauveryPink,
                            marginRight: spacing.sm,
                            fontWeight: 'bold'
                          }}>
                            •
                          </span>
                          {service}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <ConsultationFooter />
      </div>
    );
  };

  // Call Ended Page Component
  const CallEndedPage = () => {
    return (
      <div 
        className="kauvery-call-ended"
        style={{
          ...styles.body,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing.xl
        }}
      >
        <div style={{
          background: colors.white,
          borderRadius: radius.xl,
          padding: spacing['2xl'],
          maxWidth: '500px',
          textAlign: 'center',
          boxShadow: shadows.lg,
          border: `3px solid ${colors.kauveryPink}`
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: `linear-gradient(135deg, ${colors.kauveryPink}, ${colors.kauveryViolet})`,
            borderRadius: radius.full,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '32px'
          }}>
            ✅
          </div>
          
          <h1 style={{
            color: colors.kauveryPurple,
            fontSize: '28px',
            fontWeight: 700,
            margin: '0 0 16px 0',
            fontFamily: "'Poppins', sans-serif"
          }}>
            Consultation Completed
          </h1>
          
          <p style={{
            color: colors.kauveryDarkGrey,
            fontSize: '16px',
            lineHeight: 1.6,
            margin: '0 0 24px 0'
          }}>
            Your video consultation has been successfully completed. Thank you for choosing Kauvery Hospital.
          </p>
          
          <div style={{
            background: colors.grey50,
            borderRadius: radius.lg,
            padding: spacing.lg,
            margin: '24px 0',
            border: `2px solid ${colors.grey200}`
          }}>
            <h3 style={{
              color: colors.kauveryPurple,
              fontSize: '18px',
              fontWeight: 600,
              margin: '0 0 12px 0'
            }}>
              Consultation Summary:
            </h3>
            <ul style={{
              textAlign: 'left',
              color: colors.kauveryDarkGrey,
              fontSize: '14px',
              lineHeight: 1.8,
              margin: 0,
              paddingLeft: '20px'
            }}>
              <li><strong>Doctor:</strong> {appointmentData?.doctorName || ''}</li>
              <li><strong>Department:</strong> {appointmentData?.department || 'General Medicine'}</li>
              <li><strong>Patient:</strong> {appointmentData?.username || 'Patient'}</li>
              <li><strong>Room:</strong> {appointmentData?.roomID}</li>
            </ul>
          </div>
          
          <div style={{
            display: 'flex',
            gap: spacing.md,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={() => window.location.reload()}
              style={{
                background: `linear-gradient(135deg, ${colors.kauveryPurple}, ${colors.kauveryViolet})`,
                color: colors.white,
                border: 'none',
                borderRadius: radius.lg,
                padding: `${spacing.md} ${spacing.xl}`,
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              🔄 Start New Consultation
            </button>
            
            <button 
              onClick={() => window.history.back()}
              style={{
                background: colors.grey200,
                color: colors.kauveryDarkGrey,
                border: 'none',
                borderRadius: radius.lg,
                padding: `${spacing.md} ${spacing.xl}`,
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              🔙 Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show access denied page if validation failed or decryption error
  
  if (accessDenied || decodingError) {
    return <AccessDeniedPage />;
  }

  // Show health packages page if call ended and health packages should be shown
  if (showHealthPackages) {
    return <HealthPackagesPage />;
  }
  
  // Show call ended page if call has been completed
  if (callEnded) {
    return <CallEndedPage />;
  }

  // Show loading if appointment data is not yet loaded or if decryption is in progress
  if (!appointmentData || isDecoding) {
    return (
      <div style={{
        ...styles.body,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          color: colors.kauveryPurple
        }}>
          <div style={{
            width: '120px',
            height: '60px',
            background: 'transparent',
            borderRadius: radius.md,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            overflow: 'hidden'
          }}>
            <img 
              src={logoImage} 
              alt="Kauvery Hospital Logo" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
          <h2 style={{ margin: '0 0 12px 0', fontSize: '24px' }}>Kauvery Hospital</h2>
          <p style={{ margin: 0, opacity: 0.8 }}>
            {isDecoding ? 'Decrypting appointment details...' : 'Validating appointment details...'}
          </p>
        </div>
      </div>
    );
  }

  // Clear session and redirect to Kauvery Hospital after call ends
  const completeSessionAndRedirect = () => {
    try {
      sessionStorage.removeItem('consultationAccessToken');
      sessionStorage.removeItem('consultationLinkHash');
      sessionStorage.removeItem('decryptedParams');
      sessionStorage.removeItem('authToken');
      if (typeof AppointmentService?.clearAppointmentData === 'function') {
        AppointmentService.clearAppointmentData();
      } else {
        sessionStorage.removeItem('appointment_id');
      }
    } catch (e) {
    }
    window.location.href = 'https://kauveryhospital.com';
  };

  // Function to track video call events
  const trackVideoCallEvent = async (eventType, eventData = {}) => {
    try {
      const eventPayload = {
        appointment_id: appointmentData?.id,
        event_type: eventType,
        event_timestamp: new Date().toISOString(),
        event_data: JSON.stringify(eventData),
        roomID: appointmentData?.roomID,
        user_id: appointmentData?.userid,
        username: appointmentData?.username
      };

      await AppointmentService.storeVideoCallEvent(eventPayload);
    } catch (error) {
    }
  };

  // Function to start call session
  const startCallSession = async () => {
    try {
      const sessionData = {
        appointment_id: appointmentData?.id,
        session_start: new Date().toISOString(),
        roomID: appointmentData?.roomID,
        user_id: appointmentData?.userid,
        username: appointmentData?.username,
        status: 'active'
      };
      const result = await AppointmentService.startCallSession(sessionData);
      
      if (result.success) {
        setSessionId(result.session_id);
        setConnectionStartTime(new Date());
        setConnectionStatus('connected');
      }
    } catch (error) {
    }
  };

  // Function to end call session
  const endCallSession = async (reason = 'manual_end') => {
    try {
      const sessionData = {
        appointment_id: appointmentData?.id,
        session_end: new Date().toISOString(),
        roomID: appointmentData?.roomID,
        user_id: appointmentData?.userid,
        username: appointmentData?.username,
        status: 'ended',
        end_reason: reason
      };
      
      const result = await AppointmentService.endCallSession(sessionData);
      
      if (result.success) {
        setConnectionStatus('disconnected');
        setConnectionStartTime(null);
        setSessionId(null);        
      }
    } catch (error) {
    }
  };

  // Function to handle connection events
  const handleConnectionEvent = (eventType, eventData = {}) => {
    
    switch (eventType) {
      case 'connected':
        setConnectionStatus('connected');
        setConnectionStartTime(new Date());
        setReconnectCount(0);
        trackVideoCallEvent('connect', {
          ...eventData,
          connection_time: new Date().toISOString()
        });
        break;
        
      case 'disconnected':
        setConnectionStatus('disconnected');
        if (connectionStartTime) {
          const duration = Math.floor((new Date() - connectionStartTime) / 1000);
          trackVideoCallEvent('disconnect', {
            ...eventData,
            duration_seconds: duration,
            disconnect_time: new Date().toISOString()
          });
        }
        endCallSession('disconnected');
        break;
        
      case 'reconnecting':
        setConnectionStatus('reconnecting');
        setReconnectCount(prev => prev + 1);
        trackVideoCallEvent('reconnect_attempt', {
          ...eventData,
          reconnect_count: reconnectCount + 1,
          reconnect_time: new Date().toISOString()
        });
        break;
        
      case 'reconnected':
        setConnectionStatus('connected');
        trackVideoCallEvent('reconnect_success', {
          ...eventData,
          reconnect_count: reconnectCount,
          reconnect_time: new Date().toISOString()
        });
        break;
        
      case 'connection_failed':
        setConnectionStatus('failed');
        trackVideoCallEvent('connection_failed', {
          ...eventData,
          failure_time: new Date().toISOString()
        });
        break;
        
      case 'user_joined':
        setParticipantCount(prev => prev + 1);
        trackVideoCallEvent('user_joined', {
          ...eventData,
          participant_count: participantCount + 1,
          join_time: new Date().toISOString()
        });
        break;
        
      case 'user_left':
        setParticipantCount(prev => Math.max(0, prev - 1));
        trackVideoCallEvent('user_left', {
          ...eventData,
          participant_count: Math.max(0, participantCount - 1),
          leave_time: new Date().toISOString()
        });
        break;
    }
  };
  
  // Update the status display logic to use real-time participant count
  const updateStatusDisplay = () => {
    // Use the correct selectors that match your actual DOM elements
    const statusText = document.getElementById('status-text');
    const participantsList = document.getElementById('participants-list');
    
    if (!statusText || !participantsList) {
      return;
    }
    
    // Use the current participant count from state
    const currentCount = participantCount;
    
    if (currentCount === 0) {
      statusText.textContent = ' No one else in the room';
      participantsList.style.display = 'none';
    } else if (currentCount === 1) {
      statusText.textContent = '🟢 1 participant in the room';
      participantsList.style.display = 'flex';
    } else {
      statusText.textContent = `🟢 ${currentCount} participants in the room`;
      participantsList.style.display = 'flex';
    }
  };

  // Call this function whenever participant count changes
 

  return (
    <div className="video-consultation-page" style={styles.body}>
      {/* Header - same as OTP page */}
      <ConsultationHeader />

            {/* Zego Video Container with Error Boundary */}
      <VideoErrorBoundary>
        <ZegoVideoInterface 
          containerRef={zegoContainerRef}
          isInitialized={zegoInitialized}
          initializationError={initializationError}
          appointmentData={appointmentData}
          onRetry={() => {
            setInitializationError(null);
            setZegoInitialized(false);
            zegoInstanceRef.current = null;
            initializeZego().catch(() => {});
          }}
          showLeaveRoomPopup={showLeaveRoomPopup}
          onConfirmLeaveRoom={handleConfirmLeaveRoom}
          onCancelLeaveRoom={handleCancelLeaveRoom}
        />
      </VideoErrorBoundary>

      {/* Notification */}
      <div style={styles.notification}>
        <span>{notification.message}</span>
      </div>

      {/* Footer - same as OTP page */}
      <ConsultationFooter />
      

    </div>
  );
};

export default VideoConsultation; 